import React, { useState, useRef } from "react";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Box, IconButton, Button, FormControl, CircularProgress } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Controller, useForm } from "react-hook-form";

export default function DxfFileUploader({
  name,
  label,
  hasLabel = true,
  isMandatory = false,
  validationMsg,
  required,
  showError,
  setFile,
  control
}) {
  // const { control } = useForm();
  const fileInputRef = useRef();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setShowToast(null);

    const allowedFormats = [".dxf", ".DXF"];
    const fileExtension = file.name.split(".").pop();
    const maxSizeInBytes = 300 * 1024 * 1024; // 300 MB

    if (!allowedFormats.includes(`.${fileExtension}`)) {
      setShowToast({ error: true, label: "Please select a file with a valid format (.dxf)." });
      setIsLoading(false);
      return;
    }

    if (file.size > maxSizeInBytes) {
      setShowToast({ error: true, label: "File size exceeds the maximum limit of 300 MB." });
      setIsLoading(false);
      return;
    }

    setUploadedFile(file);
    setFile(file);
    setIsLoading(false);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFile(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <div className="inputContainer" style={{ alignItems: "flex-start" }}>
        {hasLabel && (
          <h3 className="label" style={{ width: "100%" }}>
            {label}
            {isMandatory && <span className="compulsoryField"> *</span>}
          </h3>
        )}

        <Box sx={{ flexDirection: "column", position: "relative", width: "100%", display: "flex" }}>
          <FormControl fullWidth sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              component="span"
              onClick={() => fileInputRef.current.click()}
              sx={{
                width: "100%",
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                height: "30px",
                backgroundColor: "rgb(21, 124, 179)",
                color: "white",
                "&:hover": { backgroundColor: "#004e6c", color: "#fff" },
              }}
              disabled={uploadedFile !== null}
            >
              {isLoading ? (
                <Box sx={{ justifyContent: "center", position: "absolute", alignItems: "center", display: "flex", inset: 0 }}>
                  <CircularProgress sx={{ color: "#ffffff" }} size={28} />
                </Box>
              ) : (
                <React.Fragment>
                  <CloudUploadOutlinedIcon />
                  <span style={{ position: "absolute", fontSize: "0.5rem", marginLeft: "8em" }}>{validationMsg}</span>
                </React.Fragment>
              )}
            </Button>

            <Controller
              name={name}
              control={control}
              rules={{ required: required ? `${label || "This field"} is required` : false }}
              render={({ field, fieldState }) => (
                <React.Fragment>
                  <input
                    name={field.name}
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      field.onChange(e.target.files);
                      handleFileUpload(e);
                    }}
                  />
                  {fieldState?.error && (
                    <span className="errorText">{fieldState.error.message}</span>
                  )}
                </React.Fragment>
              )}
            />
          </FormControl>
          {required && showError && !uploadedFile && <span className="errorText">{`${hasLabel ? label : "Above"} document is required.`}</span>}
          {!!showToast?.error && <span className="errorText">{showToast.label}</span>}
        </Box>
      </div>

      {uploadedFile && (
        <Box sx={{ maxWidth: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              justifyContent: "space-between",
              p: "0.25rem 0 0.25rem 0.75rem",
              backgroundColor: "#eee",
              borderRadius: "0.25rem",
              wordBreak: "break-all",
              alignItems: "center",
              display: "flex",
              lineHeight: 1,
              flex: 1,
              mb: 0.5,
              mt: 1,
              height: "18px",
              fontSize: "0.6rem",
            }}
          >
            {uploadedFile.name}
            <IconButton size="small" color="error" sx={{ fontSize: "1rem" }} onClick={handleRemoveFile}>
              <DeleteForeverIcon />
            </IconButton>
          </Box>
        </Box>
      )}


    </Box>
  );
}
